import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import {
  lidSendBlockedDetail,
  reactToMessage,
  type Message,
  type Reaction,
} from '@/features/chat/chat.api';
import { chatKeys } from '@/features/chat/keys';
import type { InfiniteMessages } from '@/features/chat/useSendMessage';
import { useAuthStore } from '@/store/auth';

/**
 * React to a message with an emoji — optimistic, WhatsApp semantics.
 *
 * Server truth (`POST /api/v1/messages/{id}/reaction`): one reaction per
 * reactor per message; a new emoji REPLACES the caller's previous one; the
 * EMPTY STRING removes it. The backend delivers to WhatsApp FIRST and
 * persists only on success, so an HTTP error means nothing changed
 * server-side — the optimistic patch must be rolled back exactly.
 *
 * Cache conventions follow `useSendMessage`:
 *   - `onMutate` cancels in-flight refetches for the conversation, snapshots
 *     the TARGET MESSAGE's reactions (not the whole cache — a whole-cache
 *     restore would clobber unrelated concurrent WS patches), and applies
 *     the local replace/remove.
 *   - `onSuccess` writes the server's authoritative `reactions` list over
 *     the optimistic one (in place — same array index, same React key).
 *   - `onError` restores the snapshot — and, for the Hub's 409 LID send
 *     guard and account-not-connected guard specifically, surfaces one
 *     quiet toast (see the onError comment for why the third 409 cause
 *     stays silent).
 *   - No `invalidateQueries`, ever — same reasoning as `useSendMessage`.
 */

export interface ReactToMessageVariables {
  /** Conversation the message lives in — locates the cache slot. */
  conversationId: string;
  /** The target message's internal id. */
  messageId: string;
  /** Emoji to set; '' removes the agent's reaction. */
  emoji: string;
}

interface ReactMutationContext {
  /** The target message's reactions as they were before the optimistic
   *  patch (`undefined` = the field was absent). Restored on error. */
  previousReactions: Reaction[] | undefined;
}

/**
 * Replace one message's `reactions` array in the infinite-messages cache,
 * in place (same page object identity discipline as `mergeServerMessage`).
 *
 * This is the ONE true write path for reactions on the chat cache — used by
 * the optimistic patch, the mutation's success/rollback paths AND the WS
 * `message.reaction` projector, so they can never disagree on shape.
 */
export function patchMessageReactions(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
  reactions: Reaction[] | undefined,
): void {
  const key = chatKeys.messages(conversationId);
  // Preserve the slot's freshness clock (same discipline as `patchCacheWith`):
  // a reaction patch is an incremental write, not a full sync — without the
  // explicit `updatedAt`, the write would re-stamp `dataUpdatedAt = now` and
  // could mark a GC'd-then-rebuilt partial cache "fresh" again, suppressing
  // the mount refetch that restores the missing history.
  const state = queryClient.getQueryState<InfiniteMessages>(key);
  queryClient.setQueryData<InfiniteMessages>(
    key,
    (old) => {
      if (!old) return old;
      let patched = false;
      const newPages = old.pages.map((page) => {
        if (patched) return page;
        const idx = page.items.findIndex((m) => m.id === messageId);
        if (idx === -1) return page;
        patched = true;
        const items = page.items.slice();
        items[idx] = { ...items[idx], reactions };
        return { ...page, items };
      });
      return patched ? { ...old, pages: newPages } : old;
    },
    { updatedAt: state?.dataUpdatedAt ?? 0 },
  );
}

/** Read one message's current reactions out of the cache (`undefined` when
 *  the message isn't cached or carries no reactions field). */
function readMessageReactions(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
): Reaction[] | undefined {
  const cache = queryClient.getQueryData<InfiniteMessages>(
    chatKeys.messages(conversationId),
  );
  if (!cache) return undefined;
  for (const page of cache.pages) {
    const found = page.items.find((m) => m.id === messageId);
    if (found) return found.reactions;
  }
  return undefined;
}

/**
 * Compute the local (optimistic) result of the agent reacting with `emoji`:
 * drop the agent's previous entry, then append the new one unless removing.
 * Mirrors the backend's upsert/delete on `(message, reactor_identity)`.
 */
export function applyLocalAgentReaction(
  reactions: Reaction[] | undefined,
  agentId: string,
  emoji: string,
): Reaction[] {
  const withoutOwn = (reactions ?? []).filter(
    (r) => !(r.reactor_kind === 'agent' && r.reactor_agent_id === agentId),
  );
  if (emoji === '') {
    return withoutOwn;
  }
  return [
    ...withoutOwn,
    { emoji, reactor_kind: 'agent', reactor_agent_id: agentId },
  ];
}

export function useReactToMessage() {
  const queryClient = useQueryClient();
  const agent = useAuthStore((s) => s.agent);

  return useMutation<Message, unknown, ReactToMessageVariables, ReactMutationContext>({
    mutationFn: ({ messageId, emoji }) => reactToMessage({ messageId, emoji }),

    onMutate: async (variables) => {
      const key = chatKeys.messages(variables.conversationId);
      // Same guard as useSendMessage: a refetch already on the wire must not
      // stomp the optimistic patch the moment it lands.
      await queryClient.cancelQueries({ queryKey: key });

      const previousReactions = readMessageReactions(
        queryClient,
        variables.conversationId,
        variables.messageId,
      );
      patchMessageReactions(
        queryClient,
        variables.conversationId,
        variables.messageId,
        applyLocalAgentReaction(previousReactions, agent?.id ?? '', variables.emoji),
      );
      return { previousReactions };
    },

    onSuccess: (serverMessage, variables) => {
      // The server list is authoritative (it may include other reactors'
      // entries the optimistic patch didn't know about).
      patchMessageReactions(
        queryClient,
        variables.conversationId,
        variables.messageId,
        serverMessage.reactions ?? [],
      );
    },

    onError: (error, variables, context) => {
      // The backend persists nothing on failure (gateway-first ordering), so
      // restoring the snapshot returns the cache to exact server truth.
      patchMessageReactions(
        queryClient,
        variables.conversationId,
        variables.messageId,
        context?.previousReactions,
      );

      // 409 = the Hub refused up front. This endpoint has three causes, so
      // the FastAPI `{"detail": ...}` body is the discriminator:
      //   - LID send guard — the customer is known only by WhatsApp LID and
      //     the reaction would be misrouted; the detail is a ready-made
      //     Chinese instruction, toasted verbatim with the SAME id and
      //     duration as useSendMessage's (a send-then-react burst on the
      //     same customer replaces the one toast).
      //   - account-not-connected guard — detail contains "is not connected".
      //     Toast it: the rollback alone makes the emoji silently vanish,
      //     which read as a mysterious no-op; the operator needs to know the
      //     LINE is dead (same wording family as useSendMessage's toast, and
      //     the same stable-id replace-don't-stack behavior).
      //   - target has no wamid yet (a still-queued outbound) — a transient
      //     ordering hiccup that resolves itself when the send is acked;
      //     stays quiet, as it always has (no new UI noise).
      if (isAxiosError<{ detail?: unknown }>(error) && error.response?.status === 409) {
        const detail = error.response.data?.detail;
        const lidDetail = lidSendBlockedDetail(error);
        if (lidDetail) {
          toast(lidDetail, { id: 'lid-send-blocked', duration: 10_000 });
        } else if (typeof detail === 'string' && detail.includes('is not connected')) {
          toast('该账号当前未连接，无法发送表情回应', {
            id: 'react-account-not-connected',
          });
        }
      }
    },
  });
}
